import { APP_SETTINGS } from "../shared/app_settings";
import { commonSymbol, currencySign, formatMoney, jobOwed, jobPrice, jobSign } from "../shared/money";
import { StudioJob, TaskStatus } from "../shared/types";
import { KanbanColumn } from "./kanban";
import { cachedSettings } from "./settings_cache";
import { t } from "./i18n";

// one commission as a board card, shared by the commissions board and the tasks board

// the status colours the commissions list and its charts already use
export const COMM_COLORS: Record<string, string> = { sketch: "var(--ink-3)", wip: "var(--violet)", review: "var(--amber)", done: "var(--chi)" };

// the tasks board has four columns, a commission's statuses ride on them: review is the blocked one
export const COMM_TO_TASK: Record<string, TaskStatus> = { sketch: "todo", wip: "doing", review: "blocked", done: "done" };
export const TASK_TO_COMM: Record<TaskStatus, string> = { todo: "sketch", doing: "wip", blocked: "review", done: "done" };
export const commTaskStatus = (job: StudioJob): TaskStatus => COMM_TO_TASK[job.status] ?? "todo";

export function commColumns(): KanbanColumn[] {
  return APP_SETTINGS.jobStatuses.map((id) => ({ id, label: t(id), color: COMM_COLORS[id] }));
}

// the sign picked in setup, else the one most used in these amounts
let pickedSign = "";
let signLoaded = false;
export async function loadCommSign(): Promise<void> {
  if (signLoaded) return;
  signLoaded = true;
  pickedSign = currencySign((await cachedSettings().catch(() => null))?.currency, "");
}
window.addEventListener("chi-currency", (event) => {
  pickedSign = currencySign((event as CustomEvent<string>).detail, "");
  signLoaded = true;
});
export function commSign(jobs: StudioJob[]): string {
  return pickedSign || commonSymbol(jobs.map((job) => job.amount));
}

export interface CommTileOptions {
  job: StudioJob;
  sign: string;
  // the "commission" badge, for the tasks board where it sits among tasks
  badge?: boolean;
  // shows the commission's own status as a small label (the tasks board folds four statuses into its columns)
  showStatus?: boolean;
  open?: () => void;
  move?: () => void;
  trash?: HTMLElement | null;
}

export function buildCommTile(options: CommTileOptions): HTMLLIElement {
  const { job } = options;
  const sign = jobSign(job, options.sign);
  const tile = document.createElement("li");
  tile.className = "task-tile comm-tile from-commission";
  if (options.open) tile.addEventListener("click", options.open);

  const top = document.createElement("div");
  top.className = "task-tile-top";
  if (options.badge) {
    const badge = document.createElement("span");
    badge.className = "badge commission";
    badge.textContent = t("commission");
    top.append(badge);
  }
  const title = document.createElement("span");
  title.className = "card-title";
  title.textContent = job.name;
  top.append(title);

  const client = job.client && job.client.toLowerCase() !== job.name.toLowerCase() ? t("for {client}", { client: job.client }) : "";
  const meta = document.createElement("span");
  meta.className = "card-meta";
  meta.textContent = [client, job.due && t("due {date}", { date: job.due }), options.showStatus && job.status === "review" && t("review")].filter(Boolean).join(" · ");

  tile.append(top, meta);

  const price = jobPrice(job);
  const owed = jobOwed(job);
  const money = document.createElement("span");
  money.className = "card-meta comm-money";
  const amount = document.createElement("b");
  amount.textContent = price ? formatMoney(price, sign) : job.amount;
  money.append(amount);
  if (price && owed > 0) {
    const left = document.createElement("span");
    left.className = "money-owed";
    left.textContent = t("{owed} owed", { owed: formatMoney(owed, sign) });
    money.append(left);
  } else if (price) {
    const settled = document.createElement("span");
    settled.className = "money-credit";
    settled.textContent = t("paid");
    money.append(settled);
  }
  if (amount.textContent) tile.append(money);

  if (options.move || options.trash) {
    const foot = document.createElement("div");
    foot.className = "task-tile-foot";
    if (options.move) {
      const move = document.createElement("button");
      move.type = "button";
      move.className = "pill task-move";
      move.textContent = `${t("move")} ▾`;
      move.setAttribute("aria-label", t("move to"));
      const run = options.move;
      move.addEventListener("click", (event) => {
        event.stopPropagation();
        run();
      });
      foot.append(move);
    }
    if (options.trash) foot.append(options.trash);
    tile.append(foot);
  }
  return tile;
}
